import { ref } from 'vue';
import apiClient from '@/scripts/api/apiClient';
import { handleApiError } from '@/scripts/shared-utils';
import { queryClient } from '@/scripts/queryClient';
import router from '@/scripts/router';

export const authState = {
  isLoggedIn: ref(false),
  permissions: ref([]),
  userId: ref(null),
  userName: ref(null),
  isEmailVerified: ref(false),
  requestInterceptor: ref(null),
  responseInterceptor: ref(null),
  userInfoRefreshTimer: ref(null)
};

export const login = async (email, password) => {
  try {
    const response = await apiClient.post('/api/login', { email, password });
    if (response.data.token) {
      setAuthToken(response.data.token);
      const success = await updateUserInfo();
      if (success) {
        startUserInfoUpdates();
      }
      return success;
    }
    return false;
  } catch (error) {
    handleApiError(error, 'login attempt');
    logout();
    return false;
  }
};

export const logout = () => {
  stopUserInfoUpdates();
  setAuthToken(null);
  queryClient.clear();
  router.push('/login');
};

const startUserInfoUpdates = () => {
  stopUserInfoUpdates();
  authState.userInfoRefreshTimer.value = setInterval(() => {
    if (authState.isLoggedIn.value) {
      updateUserInfo();
    } else {
      // If somehow the user is no longer logged in, stop the timer
      stopUserInfoUpdates();
    }
  }, 60000); // 1 minute (ms)
};

const stopUserInfoUpdates = () => {
  if (authState.userInfoRefreshTimer.value) {
    clearInterval(authState.userInfoRefreshTimer.value);
    authState.userInfoRefreshTimer.value = null;
  }
};

export const updateUserInfo = async () => {
  try {
    const response = await apiClient.get('/api/users/me');
    authState.isEmailVerified.value = response.data?.verified_email || false;
    authState.userId.value = response.data?.id;
    if (response.data?.first_name) {
      authState.userName.value = response.data.first_name;
    } else {
      authState.userName.value = response.data?.email;
    }
    authState.permissions.value = response.data?.permissions || [];
    localStorage.setItem('permissions', JSON.stringify(authState.permissions.value));
    return true;
  } catch (error) {
    handleApiError(error, 'fetching user info');
  }
  logout();
  return false;
};

const clearUserInfo = () => {
  authState.userId.value = null;
  authState.userName.value = null;
  authState.isEmailVerified.value = false;
  authState.permissions.value = [];
  localStorage.removeItem('permissions');
  authState.isLoggedIn.value = false;
};

// Parse JWT token without validation
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

// Check if token needs renewal (within last 50% of lifetime)
const shouldRenewToken = (token) => {
  // No renewal for superusers
  if (authState.permissions.value.includes(`system:superuser`)) return false;
  
  if (!token) return false;
  
  const payload = parseJwt(token);
  if (!payload?.exp || !payload?.iat) return false;
  
  const now = Date.now() / 1000;
  const exp = payload.exp;
  if (now >= exp) return false;

  const totalLifetime = exp - payload.iat;
  const inRenewalWindow = (exp - now) < totalLifetime * 0.5;
  
  return inRenewalWindow;
};

// Renew token if needed
const renewToken = async () => {
  try {
    const response = await apiClient.post('/api/renew-token');
    if (response.data?.token) {
      setAuthToken(response.data.token);
      return true;
    }
  } catch (error) {
    // If renewal fails, we'll keep using the current token
    console.warn('Token renewal failed:', error.response?.data?.error || error.message);
  }
  return false;
};

const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);

    // Add a request interceptor for token renewal and header management
    const interceptor = apiClient.interceptors.request.use(async (config) => {
      if (config.url.startsWith('/api/')) {
        var currentToken = localStorage.getItem('token');
        if (currentToken) {
          if (shouldRenewToken(currentToken) && !config.url.startsWith('/api/renew-token')) {
            await renewToken();
            currentToken = localStorage.getItem('token');
          }
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
      }
      return config;
    }, (error) => {
      return Promise.reject(error);
    });

    // Add a response interceptor to handle 401 Unauthorized responses
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle 401 Unauthorized errors
        // Avoid infinite loops by handling 401s from explicit API prefixes
        const apiPrefixes = ['/api/users', '/api/organizations', '/api/channels', '/api/videos', '/api/video-files'];
        if (error.response && error.response.status === 401 && 
            apiPrefixes.some(prefix => error.config.url.startsWith(prefix))) {
          console.warn('Received 401 Unauthorized response, logging out');
          logout();
        }
        return Promise.reject(error);
      }
    );

    // Store interceptor references for cleanup
    if (!authState.requestInterceptor.value) {
      authState.requestInterceptor.value = interceptor;
    }
    if (!authState.responseInterceptor.value) {
      authState.responseInterceptor.value = responseInterceptor;
    }

    let initialPermissions = [];
    try {
      const localPermissions = localStorage.getItem('permissions');
      initialPermissions = JSON.parse(localPermissions);
      if (!Array.isArray(initialPermissions)) {
        initialPermissions = [];
        localStorage.removeItem('permissions');
      }
    } catch {
      initialPermissions = [];
    }
    authState.permissions.value = initialPermissions;
    authState.isLoggedIn.value = true;
  } else {
    localStorage.removeItem('token');
    
    // Remove interceptors
    if (authState.requestInterceptor.value) {
      apiClient.interceptors.request.eject(authState.requestInterceptor.value);
      authState.requestInterceptor.value = null;
    }
    if (authState.responseInterceptor.value) {
      apiClient.interceptors.response.eject(authState.responseInterceptor.value);
      authState.responseInterceptor.value = null;
    }

    clearUserInfo();
  }
};

export const registerUser = async (userData) => {
  const response = await apiClient.post('/api/register', userData);
  return response.data;
};

export const verifyEmail = async (user_id, token) => {
  const defaultError = 'An error occurred while verifying the email';
  try {
    const response = await apiClient.post('/api/verify-email', { user_id, token });
    if (response.status === 200) {
      return true;
    }
    return response.data?.error || defaultError;
  } catch (error) {
    handleApiError(error, 'verifying email');
    return error.response?.data?.error || defaultError;
  }
};

export const resendVerification = async () => {
  const defaultError = 'An error occurred while sending the verification email';
  try {
    const response = await apiClient.post('/api/resend-verification');
    if (response.status === 200) {
      return true;
    }
    return response.data?.error || defaultError;
  } catch (error) {
    handleApiError(error, 'sending verification email');
    return error.response?.data?.error || defaultError;
  }
};

export const forgotPassword = async (email) => {
  const defaultError = 'An error occurred while sending the password reset email';
  try {
    const response = await apiClient.post('/api/forgot-password', { email });
    if (response.status === 200) {
      return true;
    }
    return response.data?.error || defaultError;
  } catch (error) {
    handleApiError(error, 'sending password reset email');
    return error.response?.data?.error || defaultError;
  }
};

export const resetPassword = async (user_id, email, password, token) => {
  const defaultError = 'An error occurred while resetting the password';
  try {
    const response = await apiClient.post('/api/reset-password', {
      user_id,
      email,
      password,
      token,
    });
    if (response.status === 200) {
      return true;
    }
    return response.data?.error || defaultError;
  } catch (error) {
    handleApiError(error, 'resetting password');
    return error.response?.data?.error || defaultError;
  }
};

export const changePassword = async (oldPassword, newPassword) => {
  const defaultError = 'An error occurred while changing the password';
  try {
    const response = await apiClient.post('/api/users/me/password', {
      password: oldPassword,
      new_password: newPassword,
    });
    if (response.status === 200) {
      return true;
    }
    return response.data?.error || defaultError;
  } catch (error) {
    handleApiError(error, 'changing password');
    return error.response?.data?.error || defaultError;
  }
};

// Initialize auth state
const initializeAuth = async () => {
  const token = localStorage.getItem('token');
  if (token) {
    setAuthToken(token);
    const success = await updateUserInfo();
    if (success) {
      startUserInfoUpdates();
    }
  } else {
    logout();
  }
};

initializeAuth();
